/**
 * Styled Components Patch
 *
 * This file provides a global theme for styled-components to prevent
 * the "Cannot read properties of undefined (reading 'theme')" error.
 *
 * It patches the styled-components library to use a default theme when
 * the ThemeProvider is not available or when there's a nesting issue.
 */

import defaultTheme from './styled-components-theme';
import { Theme } from '../types/styled';

/**
 * Make the theme globally available
 * This ensures that styled-components always has access to a theme,
 * even if the ThemeProvider is not available or there's a nesting issue.
 */
declare global {
  interface Window {
    __STYLED_COMPONENTS_THEME__: Theme;
  }
}

/**
 * Set the default theme for styled-components
 * This is used when the ThemeProvider is not available
 */
if (typeof window !== 'undefined') {
  window.__STYLED_COMPONENTS_THEME__ = defaultTheme;
}

/**
 * Get the current theme
 * This function returns the current theme from the ThemeProvider
 * or the default theme if the ThemeProvider is not available
 *
 * @returns The current theme
 */
export const getCurrentTheme = (): Theme => {
  if (typeof window !== 'undefined' && window.__STYLED_COMPONENTS_THEME__) {
    return window.__STYLED_COMPONENTS_THEME__;
  }
  
  return defaultTheme;
};

/**
 * Patch styled-components to use the default theme
 * This function is called automatically when this file is imported
 */
const patchStyledComponents = () => {
  if (typeof window === 'undefined') return;
  
  // Store the original getTheme function
  const originalGetTheme = (window as any).__STYLED_COMPONENTS_GETTHEME__;
  
  // Create a new getTheme function that uses the default theme as a fallback
  const patchedGetTheme = (props: any) => {
    try {
      // Try to get the theme from the original function
      const theme = originalGetTheme ? originalGetTheme(props) : undefined;
      
      // If the theme is undefined, use the default theme
      if (theme === undefined) {
        return window.__STYLED_COMPONENTS_THEME__;
      }
      
      return theme;
    } catch (error) {
      // If there's an error, use the default theme
      console.warn('Error getting theme from styled-components, using default theme', error);
      return window.__STYLED_COMPONENTS_THEME__;
    }
  };
  
  // Replace the original getTheme function with the patched one
  (window as any).__STYLED_COMPONENTS_GETTHEME__ = patchedGetTheme;
};

// Apply the patch
patchStyledComponents();

export default defaultTheme;